import _ from "lodash";
import * as Eden from "src/eden/content";
import * as Event from "src/state/modules/event";

const getMapsURL = (location: Eden.Location): string | undefined => {
  const google_place_details = location.google_place_details;
  if (google_place_details) {
    const query = google_place_details.location_name
      ? encodeURIComponent(google_place_details.location_name)
      : `${google_place_details.lat_long?.lat},${google_place_details.lat_long?.long}`;

    return `https://www.google.com/maps/search/?api=1&query=${query}&query_place_id=${google_place_details.id}`;
  }

  const coordinates = location.coordinates;
  if (coordinates) {
    return `https://www.google.com/maps/search/?api=1&query=${coordinates.latitude},${coordinates.longitude}`;
  }
  return undefined;
};

const getPlaceID = (location: Eden.Location): string | undefined =>
  location.google_place_details?.id ?? location.place_id ?? undefined;

// From Eden src/services/user_notification/transaction_email/custom_filters.ts
function getAddressStrings(placeDetail: Eden.GooglePlaceDetails): string[] {
  let locationName: string | undefined;

  // if the placeDetail.type is a street address it means the name will be the street address,
  // which we're adding in directly below the name, so we dont want to use the name in that case.
  if (!_.includes(placeDetail.types, "street_address")) {
    locationName = placeDetail.location_name;
  }
  const address = placeDetail.address_object;

  const streetAddress = _.compact([address?.street_number, address?.street_name]).join(" ").trim();
  const cityStateZip = _.compact([address?.city, address?.state, address?.postal_code]).join(" ").trim();

  const addressComponents = [locationName, streetAddress, cityStateZip];
  return _.filter(addressComponents, (addressLine): addressLine is string => !_.isEmpty(addressLine));
}

export const mapFromLocation = (
  edenLocation: Eden.Location | null | undefined
): { location: Event.Location | undefined; addressParts: string[] } => {
  // GameChanger sends two flavors of location data: rich venues carry a
  // `google_place_details` object, while lightweight venues only carry a flat
  // `address` array (e.g. ["1555 Recreation Dr", "DeKalb IL"]) plus coordinates
  // and a `name`. Prefer the populated `address` array over the `name`-only
  // fallback so the city/state still shows (matching the GC app).
  const addressParts =
    edenLocation && edenLocation.google_place_details
      ? getAddressStrings(edenLocation.google_place_details)
      : edenLocation && !_.isEmpty(edenLocation.address)
      ? _.compact(edenLocation.address)
      : edenLocation && edenLocation.name
      ? [edenLocation.name, "No address"]
      : edenLocation && edenLocation.coordinates
      ? ["Pinned Location"]
      : ["No location"];

  // Prefer flat `coordinates`, but fall back to the rich venue's
  // `google_place_details.lat_long` so weather/maps work for the many venues
  // that only carry the Google place details (no top-level coordinates).
  const latLong = edenLocation?.google_place_details?.lat_long;
  const coordinates = edenLocation?.coordinates
    ? {
        latitude: _.get(edenLocation.coordinates, "latitude") ?? 0,
        longitude: _.get(edenLocation.coordinates, "longitude") ?? 0,
      }
    : latLong && latLong.lat != null && latLong.long != null
    ? { latitude: latLong.lat, longitude: latLong.long }
    : undefined;

  const location: Event.Location | undefined = edenLocation
    ? {
        mapsURL: getMapsURL(edenLocation),
        coordinates,
        placeID: getPlaceID(edenLocation),
        name: edenLocation.name ?? undefined,
      }
    : undefined;
  return { location, addressParts };
};
